import { useEffect, useState, useRef } from 'react';
//import { getOptions, useRerenderListener } from '@/lib/globals';
import { useGoogleCalendarEvents } from './useGoogleCalendarEvents.ts';
import { useMicrosoftCalendarEvents } from './useMicrosoftCalendarEvents.ts';
import { addWeeks, isAfter, isBefore, startOfDay } from 'date-fns';
import { useCoverage } from './useCoverage.ts';

import { useCommunication } from '../lib/CommunicationWrapper';

// Global fetch tracking to prevent duplicate API calls
const fetchInProgress = new Map<string, Promise<any>>();

// Unified calendar event interface
export interface CalendarEvent {
    id: string;
    title: string;
    description?: string;
    startDate: Date;
    endDate: Date;
    isAllDay: boolean;
    plannedAt: string;
    duration: number; // Duration in minutes
    location?: string;
    htmlLink?: string;
    calendarColor?: string;
    calendarId?: string;
    source: 'google_calendar' | 'microsoft_calendar';
    isGoogleEvent?: boolean;
    isMicrosoftEvent?: boolean;
}

const mapGoogleEventsToCalendarEvents = (events: any[]): CalendarEvent[] => {
    return events.map(event => {
        const eventStartDate = new Date(event.start?.dateTime || event.start?.date || 0);
        const eventEndDate = new Date(event.end?.dateTime || event.end?.date || 0);
        const isAllDay = !event.start?.dateTime;

        return {
            id: `google_${event.id}`,
            title: event.summary || 'Untitled Event',
            description: event.description,
            startDate: eventStartDate,
            endDate: eventEndDate,
            isAllDay,
            duration: Math.floor((eventEndDate.getTime() - eventStartDate.getTime()) / (1000 * 60)),
            plannedAt: eventStartDate.toISOString(),
            location: event.location,
            htmlLink: event.htmlLink,
            calendarColor: event.calendarColor,
            calendarId: event.calendarId,
            source: 'google_calendar' as const,
            isGoogleEvent: true,
            isMicrosoftEvent: false,
        };
    });
}

const mapMicrosoftEventsToCalendarEvents = (events: any[]): CalendarEvent[] => {
    return events.map(event => {
        const eventStartDate = new Date(event.start?.dateTime || event.start?.date || 0);
        const eventEndDate = new Date(event.end?.dateTime || event.end?.date || 0);
        const isAllDay = !event.start?.dateTime;

        return {
            id: `microsoft_${event.id}`,
            title: event.summary || 'Untitled Event',
            description: event.description,
            startDate: eventStartDate,
            endDate: eventEndDate,
            isAllDay,
            plannedAt: eventStartDate.toISOString(),
            duration: Math.floor((eventEndDate.getTime() - eventStartDate.getTime()) / (1000 * 60)),
            location: event.location,
            htmlLink: event.htmlLink,
            calendarColor: event.calendarColor,
            calendarId: event.calendarId,
            source: 'microsoft_calendar' as const,
            isGoogleEvent: false,
            isMicrosoftEvent: true
        };
    });
}

export const useCalendarEvents2 = (startDate?: Date, endDate?: Date) => {
    return {
        events: [],
        loading: false,
        lastSync: {
            google: null,
            microsoft: null
        },
        googleEvents: [],
        microsoftEvents: []
    }
}

/**
 * Unified hook to get calendar events from both Google and Microsoft calendars
 * Returns events in a normalized format that can be used consistently across the app
 *
 * @param startDate - Start of the date range to fetch events for
 * @param endDate - End of the date range to fetch events for
 */
export const useCalendarEvents = (startDate?: Date, endDate?: Date) => {
    const [events, setEvents] = useState<CalendarEvent[]>([]);
    const [loading, setLoading] = useState(false);
    const [lastSync, setLastSync] = useState<{ google?: number | null; microsoft?: number | null }>({});

    // this might be a local namespace
    //const { trigger } = useRerenderListener(["google_cal_last_sync", "microsoft_cal_last_sync"]);

    const { getUnfetchedRange, updateCoverageRange } = useCoverage();

    const googleCalendar = useGoogleCalendarEvents();
    const microsoftCalendar = useMicrosoftCalendarEvents();

    useEffect(() => {
        const fetchEvents = async () => {
            if (!startDate || !endDate) return;
            const rangesToFetch = getUnfetchedRange(startDate, endDate);

            if (rangesToFetch.length === 0) return;

            const allFetchedEvents: CalendarEvent[] = [];
            for (const range of rangesToFetch) {
                updateCoverageRange(range.start, range.end);
                const googleFetchedEvents = await googleCalendar.fetchEventsForDateRange(range.start);
                const microsoftFetchedEvents = await microsoftCalendar.fetchEventsForDateRange(range.start);
                const googleFormattedEvents = mapGoogleEventsToCalendarEvents(googleFetchedEvents);
                const microsoftFormattedEvents = mapMicrosoftEventsToCalendarEvents(microsoftFetchedEvents);
                allFetchedEvents.push(...googleFormattedEvents, ...microsoftFormattedEvents);
            }
            setEvents(prev => [...prev, ...allFetchedEvents]);
        }
        fetchEvents();
    }, [startDate, endDate]);

    /*
    // Initialize cached range based on the stored events (2 weeks from now)
    useEffect(() => {

        /*
        sendMessage({
            type: "CALL_EXTENSION_FUNC",
            payload: {
                func: "triggerCalendarPullSync",
                args: {}
            }
        });
        

        // The initially cached events cover today + 2 weeks
        const now = new Date();
        const initialStart = startOfDay(now);
        const initialEnd = addWeeks(initialStart, 2);

        cachedRangeRef.current = {
            start: initialStart,
            end: initialEnd
        };
    }, []);
    */

    /*
    // Check if we need to fetch additional events
    useEffect(() => {
        
        console.log("[useCalendarEvents] start/end date: ", startDate, endDate);
        if (!startDate || !endDate) return;

        const fetchAdditionalEvents = async () => {
            
            const rangesToFetch = getUnfetchedRange(startDate, endDate);
            console.log(`[useCalendarEvents] Fetching additional events for range:`, rangesToFetch);

            let acc = [];
            for (const range of rangesToFetch) {
                //updateCoverageRange(range.start, range.end);
                /*
                const googleFetchedEvents = await googleCalendar.fetchEventsForDateRange(range.start);
                const microsoftFetchedEvents = await microsoftCalendar.fetchEventsForDateRange(range.start);

                const googleFormattedEvents = mapGoogleEventsToCalendarEvents(googleFetchedEvents);
                const microsoftFormattedEvents = mapMicrosoftEventsToCalendarEvents(microsoftFetchedEvents);

                acc = acc.concat(googleFormattedEvents).concat(microsoftFormattedEvents);

                
            }

            
            //setAdditionalEvents(acc);
            

        };

        fetchAdditionalEvents();
        
    }, [startDate, endDate, getUnfetchedRange, updateCoverageRange]); // Removed googleCalendar, microsoftCalendar dependencies

    */

    /*
    // Process cached events
    useEffect(() => {
        let combinedEvents: CalendarEvent[] = [];

        combinedEvents = combinedEvents.concat(mapGoogleEventsToCalendarEvents(googleCalendar.events))
        combinedEvents = combinedEvents.concat(mapMicrosoftEventsToCalendarEvents(microsoftCalendar.events));

        // Remove duplicates based on event ID
        const uniqueEvents = combinedEvents.filter((event, index, self) =>
            index === self.findIndex(e => e.id === event.id)
        );

        // Sort all events by start time
        uniqueEvents.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());

        // Filter events based on date range if provided
        let finalEvents = uniqueEvents;
        if (startDate && endDate) {
            finalEvents = uniqueEvents.filter(event => {
                const eventStart = event.startDate;
                const eventEnd = event.endDate;
                // Check if event overlaps with the date range
                return (
                    (eventStart >= startDate && eventStart <= endDate) ||
                    (eventEnd >= startDate && eventEnd <= endDate) ||
                    (eventStart <= startDate && eventEnd >= endDate)
                );
            });
        }

        setEvents(finalEvents);
        setLoading(googleCalendar.loading || microsoftCalendar.loading);
    }, [googleCalendar.events, microsoftCalendar.events, googleCalendar.loading, microsoftCalendar.loading, additionalEvents, startDate, endDate]); // Removed 'trigger'

    */


    return {
        events,
        loading,
        lastSync,
        googleEvents: googleCalendar.events,
        microsoftEvents: microsoftCalendar.events
    };
};

/**
 * Filter calendar events by date range
 */
export const filterEventsByDateRange = (
    events: CalendarEvent[],
    startDate: Date,
    endDate: Date
): CalendarEvent[] => {
    return events.filter(event => {
        const eventStart = event.startDate;
        const eventEnd = event.endDate;

        // Check if event overlaps with the date range
        return (
            (eventStart >= startDate && eventStart <= endDate) ||
            (eventEnd >= startDate && eventEnd <= endDate) ||
            (eventStart <= startDate && eventEnd >= endDate)
        );
    });
};

/**
 * Group calendar events by date
 */
export const groupEventsByDate = (events: CalendarEvent[]): Map<string, CalendarEvent[]> => {
    const grouped = new Map<string, CalendarEvent[]>();

    events.forEach(event => {
        const dateKey = event.startDate.toISOString().split('T')[0];
        const existing = grouped.get(dateKey) || [];
        existing.push(event);
        grouped.set(dateKey, existing);
    });

    return grouped;
};

/**
 * Check if user has any calendar connected
 
export const useHasCalendarConnected = (): boolean => {
    const options = getOptions();
    return !!(options.google_cal_sync_token || options.microsoft_cal_sync_token);
};
*/